<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5">
            <p class="text-900 mb-2">Version: 1.0</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. PURPOSE AND OBJECTIVES</h2>
            <p class="text-body mb-2">1.1 ILearn’d Tutors is committed to the <b>responsible, ethical, transparent, and lawful use of Artificial Intelligence (“AI”)</b> to support educational delivery while safeguarding students, tutors, parents, institutes, and minors.</p>
            <p class="text-body mb-2">1.2 This AI Usage, Ethics & Governance Policy (“Policy”) establishes:</p>
            <p class="text-body ml-3 mb-1">(a) how AI is used on the Platform;</p>
            <p class="text-body ml-3 mb-1">(b) the limitations of AI-generated outputs;</p>
            <p class="text-body ml-3 mb-1">(c) user responsibilities when engaging with AI features;</p>
            <p class="text-body ml-3 mb-1">(d) safeguards for children and minors;</p>
            <p class="text-body ml-3 mb-1">(e) data protection and privacy obligations; and</p>
            <p class="text-body ml-3 mb-2">(f) accountability, transparency, and risk-mitigation measures.</p>
            <p class="text-body">1.3 This Policy applies to <b>all users of the Platform,</b> including students, parents or guardians, tutors, institutes, general users, and ILearn’d Tutors staff.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. ROLE AND NATURE OF AI ON THE PLATFORM</h2>
            <p class="text-body mb-2">2.1 AI on ILearn’d Tutors is <b>assistive and supportive only</b> and is intended to enhance learning, safety, and platform efficiency.</p>
            <p class="text-body mb-2">2.2 AI may be used to:</p>
            <p class="text-body ml-3 mb-1">(a) improve learning efficiency and comprehension;</p>
            <p class="text-body ml-3 mb-1">(b) support tutors with insights and summaries;</p>
            <p class="text-body ml-3 mb-1">(c) assist with scheduling, analytics, and organisation;</p>
            <p class="text-body ml-3 mb-1">(d) support safety monitoring and fraud prevention; and</p>
            <p class="text-body ml-3 mb-2">(e) provide academic guidance and learning insights.</p>
            <p class="text-body mb-1">2.3 AI <b>does not:</b></p>
            <p class="text-body ml-3 mb-1">(a) replace human tutors, teachers, or examiners;</p>
            <p class="text-body ml-3 mb-1">(b) act as a licensed educator or authority;</p>
            <p class="text-body ml-3 mb-1">(c) guarantee academic outcomes or results;</p>
            <p class="text-body ml-3 mb-1">(d) make final academic, disciplinary, or legal decisions; or</p>
            <p class="text-body ml-3">(e) substitute parental, guardian, or tutor supervision.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. AI FEATURES COVERED BY THIS POLICY</h2>
            <p class="text-body mb-2">3.1 This Policy applies to <b>all current and future AI features,</b> including but not limited to:</p>
            
            <p class="text-body font-bold mb-1">3.1 Learning & Academic Support</p>
            <p class="text-body ml-3 mb-1">(a) AI-generated class summaries and lesson notes;</p>
            <p class="text-body ml-3 mb-1">(b) key-point extraction and revision aids;</p>
            <p class="text-body ml-3 mb-1">(c) study recommendations and learning insights;</p>
            <p class="text-body ml-3 mb-3">(d) academic explanations and guidance tools.</p>

            <p class="text-body font-bold mb-1">3.2 Tutor & Institute Assistance</p>
            <p class="text-body ml-3 mb-1">(a) lesson pacing and performance insights;</p>
            <p class="text-body ml-3 mb-1">(b) teaching quality analytics;</p>
            <p class="text-body ml-3 mb-1">(c) scheduling and resource optimisation tools;</p>
            <p class="text-body ml-3 mb-3">(d) non-binding improvement suggestions.</p>

            <p class="text-body font-bold mb-1">3.3 Platform Operations & Safety</p>
            <p class="text-body ml-3 mb-1">(a) AI-assisted chat moderation;</p>
            <p class="text-body ml-3 mb-1">(b) behavioral and anomaly detection;</p>
            <p class="text-body ml-3 mb-1">(c) fraud prevention tools;</p>
            <p class="text-body ml-3 mb-3">(d) child protection and safety flagging.</p>

            <p class="text-body font-bold mb-1">3.4 Future Capabilities</p>
            <p class="text-body ml-3 mb-1">(a) adaptive learning paths;</p>
            <p class="text-body ml-3 mb-1">(b) predictive analytics;</p>
            <p class="text-body ml-3 mb-1">(c) limited virtual assistance tools;</p>
            <p class="text-body ml-3 mb-3">(d) AI analytics dashboards.</p>

            <p class="text-body font-bold mb-1">3.5 AI Session Summaries and Transcripts</p>
            <p class="text-body mb-2">The Platform may deploy AI-assisted systems that can join tutoring sessions when enabled by a session participant (tutor or student).</p>
            <p class="text-body mb-2">These systems may analyse session audio for the limited purpose of generating lesson summaries, learning insights, organisational notes, and transcripts intended to support educational review and revision.</p>
            <p class="text-body mb-2">Access to transcripts may vary depending on the user’s subscription plan.</p>
            <p class="text-body">Where transcripts are generated for users without Premium AI access, such transcripts may remain temporarily stored in a restricted state for a limited period of up to <b>ten (10) days</b> before being automatically deleted if the user does not upgrade their subscription.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. ACCURACY, LIMITATIONS & DISCLAIMERS</h2>
            <p class="text-body mb-2">4.1 AI-generated outputs:</p>
            <p class="text-body ml-3 mb-1">(a) may be incomplete, generalized, outdated, or inaccurate;</p>
            <p class="text-body ml-3 mb-1">(b) are probabilistic in nature and not deterministic; and</p>
            <p class="text-body ml-3 mb-2">(c) may contain errors, omissions, or misinterpretations.</p>
            <p class="text-body mb-2">4.2 Users must independently verify AI-generated content before relying on it for:</p>
            <p class="text-body ml-3 mb-1">(a) examinations;</p>
            <p class="text-body ml-3 mb-1">(b) homework or assignments;</p>
            <p class="text-body ml-3 mb-1">(c) academic or teaching decisions; or</p>
            <p class="text-body ml-3 mb-2">(d) learning strategies.</p>
            <p class="text-body mb-2">4.3 AI-generated materials are provided for <b>educational support only</b> and do not constitute:</p>
            <p class="text-body ml-3 mb-1">(a) official curriculum instruction;</p>
            <p class="text-body ml-3 mb-1">(b) examination marking authority;</p>
            <p class="text-body ml-3 mb-1">(c) professional teaching certification; or</p>
            <p class="text-body ml-3 mb-2">(d) guaranteed academic advice.</p>
            <p class="text-body">4.4 ILearn’d Tutors shall not be liable for academic outcomes, grades, or performance based on reliance on AI outputs.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. ACADEMIC INTEGRITY AND AI USE</h2>
            <p class="text-body mb-2">5.1 AI tools are designed to:</p>
            <p class="text-body ml-3 mb-1">(a) explain concepts;</p>
            <p class="text-body ml-3 mb-1">(b) provide hints or guidance; and</p>
            <p class="text-body ml-3 mb-2">(c) support understanding and learning.</p>
            <p class="text-body mb-2">5.2 AI tools <b>must not</b> be used to:</p>
            <p class="text-body ml-3 mb-1">(a) generate complete assignment or exam answers;</p>
            <p class="text-body ml-3 mb-1">(b) complete tests or assessments dishonestly; or</p>
            <p class="text-body ml-3 mb-2">(c) enable plagiarism or academic misconduct.</p>
            <p class="text-body mb-2">5.3 Students remain fully responsible for:</p>
            <p class="text-body ml-3 mb-1">(a) academic honesty;</p>
            <p class="text-body ml-3 mb-1">(b) compliance with school and examination rules; and</p>
            <p class="text-body ml-3 mb-2">(c) ethical learning practices.</p>
            <p class="text-body mb-2">5.4 Misuse of AI may result in:</p>
            <p class="text-body ml-3 mb-1">(a) restriction or removal of AI access;</p>
            <p class="text-body ml-3 mb-1">(b) account suspension or termination;</p>
            <p class="text-body ml-3 mb-1">(c) notification to parents, guardians, or institutes; and</p>
            <p class="text-body ml-3">(d) further disciplinary action where appropriate.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. HUMAN OVERSIGHT AND DECISION-MAKING</h2>
            <p class="text-body mb-2">6.1 AI systems on the Platform <b>do not operate autonomously.</b></p>
            <p class="text-body mb-2">6.2 All critical actions are subject to <b>mandatory human oversight,</b> including:</p>
            <p class="text-body ml-3 mb-1">(a) account suspensions or terminations;</p>
            <p class="text-body ml-3 mb-1">(b) child safety escalations;</p>
            <p class="text-body ml-3 mb-1">(c) content removal; and</p>
            <p class="text-body ml-3 mb-2">(d) dispute resolution.</p>
            <p class="text-body">6.3 AI insights are <b>advisory only</b> and do not replace human judgment or decision-making.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. AI MONITORING, SAFETY & FALSE FLAGS</h2>
            <p class="text-body mb-2">7.1 AI may analyse messages, session metadata, behavioural patterns, and usage anomalies solely for:</p>
            <p class="text-body ml-3 mb-1">(a) safety;</p>
            <p class="text-body ml-3 mb-1">(b) fraud prevention;</p>
            <p class="text-body ml-3 mb-1">(c) child protection; and</p>
            <p class="text-body ml-3 mb-2">(d) platform integrity.</p>
            <p class="text-body mb-2">7.2 Users acknowledge that:</p>
            <p class="text-body ml-3 mb-1">(a) AI systems may generate false positives;</p>
            <p class="text-body ml-3 mb-1">(b) automated alerts do not constitute proof of wrongdoing; and</p>
            <p class="text-body ml-3 mb-2">(c) human investigation will follow where necessary.</p>
            <p class="text-body">7.3 ILearn’d Tutors shall not be liable for inconvenience arising from temporary AI-generated flags where reasonable review procedures are followed.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. CHILD PROTECTION AND AI</h2>
            <p class="text-body mb-2">8.1 For users under eighteen (18) years of age:</p>
            <p class="text-body ml-3 mb-1">(a) AI monitoring thresholds are stricter;</p>
            <p class="text-body ml-3 mb-1">(b) grooming and inappropriate language patterns may be flagged;</p>
            <p class="text-body ml-3 mb-2">(c) off-platform contact attempts may be detected.</p>
            <p class="text-body mb-2">8.2 Where AI flags content suggesting abuse, grooming, exploitation, coercion, or threats, ILearn’d Tutors may:</p>
            <p class="text-body ml-3 mb-1">(a) immediately suspend relevant accounts;</p>
            <p class="text-body ml-3 mb-1">(b) conduct urgent human review; and</p>
            <p class="text-body ml-3">(c) escalate matters to the <b>Sri Lanka Police,</b> the <b>National Child Protection Authority (NCPA),</b> or other competent authorities.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. DATA USAGE, PRIVACY & COMPLIANCE</h2>
            <p class="text-body mb-2">9.1 AI systems may process limited categories of data, including:</p>
            <p class="text-body ml-3 mb-1">(a) messages exchanged on the Platform;</p>
            <p class="text-body ml-3 mb-1">(b) session metadata;</p>
            <p class="text-body ml-3 mb-1">(c) uploaded learning materials; and</p>
            <p class="text-body ml-3 mb-2">(d) anonymised performance analytics.</p>
            <p class="text-body mb-2">9.2 Where reasonably possible:</p>
            <p class="text-body ml-3 mb-1">(a) data is anonymised or aggregated;</p>
            <p class="text-body ml-3 mb-1">(b) personal identifiers are minimised; and</p>
            <p class="text-body ml-3 mb-2">(c) access is restricted to authorised personnel.</p>
            <p class="text-body mb-2">9.3 AI-related data is:</p>
            <a id="googleapidisclosure"></a>
            <p class="text-body ml-3 mb-1">(a) not sold;</p>
            <p class="text-body ml-3 mb-1">(b) not shared for advertising or profiling; and</p>
            <p class="text-body ml-3 mb-2">(c) used solely for platform improvement, safety, and learning support.</p>
            <p class="text-body">9.4 All AI-related data processing complies with the <b>Personal Data Protection Act, No. 9 of 2022 of Sri Lanka (PDPA).</b></p>
            <p class="text-body">9.5 <b>Google API Disclosure:</b> The use of raw or derived user data received from Workspace APIs will adhere to the Google User Data Policy, including the Limited Use requirements.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. USER CONSENT AND TRANSPARENCY</h2>
            <p class="text-body mb-2">10.1 Use of AI-enabled features requires <b>explicit user consent,</b> obtained during account setup or feature activation.</p>
            <p class="text-body mb-2">10.2 For minors, such consent must be provided by a <b>parent or legal guardian.</b></p>
            <p class="text-body mb-2">10.3 Users may:</p>
            <p class="text-body ml-3 mb-1">(a) opt out of certain AI features where available;</p>
            <p class="text-body ml-3 mb-1">(b) request clarification on AI-generated outputs; and</p>
            <p class="text-body ml-3">(c) request data access or deletion, subject to legal obligations.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. INTELLECTUAL PROPERTY AND AI OUTPUTS</h2>
            <p class="text-body mb-2">11.1 Users retain ownership of their original content.</p>
            <p class="text-body mb-2">11.2 AI-generated outputs are licensed to users <b>for personal educational use only.</b></p>
            <p class="text-body mb-2">11.3 ILearn’d Tutors retains ownership of:</p>
            <p class="text-body ml-3 mb-1">(a) AI models;</p>
            <p class="text-body ml-3 mb-1">(b) algorithms;</p>
            <p class="text-body ml-3 mb-1">(c) training methodologies; and</p>
            <p class="text-body ml-3 mb-2">(d) underlying systems.</p>
            <p class="text-body mb-2">11.4 AI-generated content shall not be:</p>
            <p class="text-body ml-3 mb-1">(a) resold;</p>
            <p class="text-body ml-3 mb-1">(b) commercially redistributed; or</p>
            <p class="text-body ml-3">(c) represented as official examination material.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">12. BIAS, FAIRNESS & NON-DISCRIMINATION</h2>
            <p class="text-body mb-2">12.1 ILearn’d Tutors actively seeks to:</p>
            <p class="text-body ml-3 mb-1">(a) minimise algorithmic bias;</p>
            <p class="text-body ml-3 mb-1">(b) promote fairness across language, gender, location, and background; and</p>
            <p class="text-body ml-3 mb-2">(c) continuously review AI outputs for unintended discrimination.</p>
            <p class="text-body">12.2 AI systems are periodically reviewed and refined to improve fairness and accuracy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">13. AVAILABILITY & TECHNICAL LIMITATIONS</h2>
            <p class="text-body mb-2">13.1 AI features may be:</p>
            <p class="text-body ml-3 mb-1">(a) temporarily unavailable;</p>
            <p class="text-body ml-3 mb-1">(b) affected by maintenance or updates; or</p>
            <p class="text-body ml-3 mb-2">(c) dependent on third-party systems.</p>
            <p class="text-body">13.2 ILearn’d Tutors does not guarantee uninterrupted availability of AI features.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">14. LIMITATION OF LIABILITY</h2>
            <p class="text-body mb-2">14.1 To the maximum extent permitted by law:</p>
            <p class="text-body ml-3 mb-1">(a) AI features are provided on an “as is” basis;</p>
            <p class="text-body ml-3 mb-1">(b) ILearn’d Tutors shall not be liable for decisions made based on AI outputs; and</p>
            <p class="text-body ml-3"> (c) liability is limited as set out in the User Agreement.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">15. POLICY UPDATES</h2>
            <p class="text-body mb-2">15.1 This Policy may be updated to reflect:</p>
            <p class="text-body ml-3 mb-1">(a) technological developments;</p>
            <p class="text-body ml-3 mb-1">(b) legal or regulatory changes; or</p>
            <p class="text-body ml-3 mb-2">(c) introduction of new AI features.</p>
            <p class="text-body">15.2 <b>Material changes</b> shall be notified through the Platform and, where required by law (particularly where minors are involved), shall require <b>explicit consent</b> before taking effect.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">16. GOVERNING LAW</h2>
            <p class="text-body">16.1 This Policy shall be governed by the laws of the <b>Democratic Socialist Republic of Sri Lanka.</b></p>
        </div>

        <div class="policy-section">
            <h2 class="H5-SemiBold text-primary mb-3">17. CONTACT INFORMATION</h2>
            <div class="p-3 border-round bg-faded">
                <p class="text-body font-bold mb-1"><b>ILearn’d Tutors – Legal Team</b></p>
                <p class="text-body mb-1">Email: support@ilearndsl.com</p>
                <p class="text-body mb-0">Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.text-primary { color: #1D97BD !important; }
.bg-faded { background-color: rgba(29, 151, 189, 0.04); }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.ml-3 { margin-left: 1.5rem; }
</style>